import React from "react";
import { useDraggable } from "@dnd-kit/core";
import {
  CalendarDaysIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  ScaleIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { format, isToday, isTomorrow, isPast, parseISO } from "date-fns";
import { nl } from "date-fns/locale";

export interface TaskWithUser {
  id: string;
  title: string;
  description: string;
  type: "communication" | "calibration" | "expiry_check" | "complaint" | "orders" | "other";
  status: "pending" | "in_progress" | "completed" | "cancelled";
  priority: "low" | "medium" | "high" | "urgent";
  pharmacy_id: string;
  assignment_type: "specific_user" | "open_to_all" | "team";
  assigned_to?: string;
  claimed_by?: string;
  claimed_at?: string;
  created_by: string;
  due_date: string;
  completed_at?: string;
  completed_by?: string;
  created_at: string;
  updated_at: string;
  pharmacy_name: string;
  created_by_name: string;
  assigned_to_name?: string;
  claimed_by_name?: string;
  completed_by_name?: string;
  assignment_display?: string;
  urgency_status?: "normal" | "due_soon" | "overdue";
  is_recurring?: boolean;
  recurring_task_id?: string;
  recurring_task_status?: "active" | "paused" | "completed" | "cancelled";
  recurrence_pattern?: string;
  recurrence_display?: string;
}

interface KanbanCardProps {
  task: TaskWithUser;
  canDrag: boolean;
  onClickCard: (task: TaskWithUser) => void;
  onClaimTask?: (taskId: string) => void;
  onUnclaimTask?: (taskId: string) => void;
  currentUserId?: string;
  isDragOverlay?: boolean;
}

const getTaskTypeIcon = (type: string) => {
  switch (type) {
    case "communication": return ChatBubbleLeftRightIcon;
    case "calibration": return ScaleIcon;
    case "expiry_check": return CalendarDaysIcon;
    case "complaint": return ExclamationTriangleIcon;
    case "orders": return ClipboardDocumentListIcon;
    default: return ClockIcon;
  }
};

const getTaskTypeLabel = (type: string) => {
  switch (type) {
    case "communication": return "Communicatie";
    case "calibration": return "Controle balansen";
    case "expiry_check": return "Vervaldata";
    case "complaint": return "Klacht";
    case "orders": return "Bestellingen";
    default: return "Overige";
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "urgent": return "text-red-700 bg-red-50 border-red-200";
    case "high": return "text-orange-700 bg-orange-50 border-orange-200";
    case "medium": return "text-yellow-700 bg-yellow-50 border-yellow-200";
    case "low": return "text-green-700 bg-green-50 border-green-200";
    default: return "text-gray-700 bg-gray-50 border-gray-200";
  }
};

const getPriorityLabel = (priority: string) => {
  switch (priority) {
    case "urgent": return "Urgent";
    case "high": return "Hoog";
    case "medium": return "Gemiddeld";
    case "low": return "Laag";
    default: return "Onbekend";
  }
};

const formatDueDate = (dateString: string) => {
  const date = parseISO(dateString);
  if (isToday(date)) return "Vandaag";
  if (isTomorrow(date)) return "Morgen";
  if (isPast(date)) return "Verlopen";
  return format(date, "d MMM", { locale: nl });
};

const getDueDateColor = (dateString: string) => {
  const date = parseISO(dateString);
  if (isPast(date) && !isToday(date)) return "text-red-600";
  if (isToday(date)) return "text-orange-600";
  if (isTomorrow(date)) return "text-yellow-600";
  return "text-gray-500";
};

export function KanbanCardDisplay({
  task,
  canDrag,
  onClickCard,
  onClaimTask,
  onUnclaimTask,
  currentUserId,
  isDragOverlay,
}: KanbanCardProps) {
  const TaskIcon = getTaskTypeIcon(task.type);

  return (
    <div
      onClick={() => onClickCard(task)}
      className={`group bg-white rounded-lg border border-gray-200 p-3 transition-all duration-150 ${
        canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
      } ${
        isDragOverlay
          ? "shadow-lg ring-2 ring-primary-400 rotate-1"
          : "shadow-sm hover:shadow-md hover:-translate-y-0.5"
      }`}
    >
      {/* Title + Priority */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="text-sm font-medium text-gray-900 line-clamp-2 flex-1">
          {task.title}
        </h4>
        <span
          className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${getPriorityColor(task.priority)}`}
        >
          {getPriorityLabel(task.priority)}
        </span>
      </div>

      {/* Description preview */}
      {task.description && (
        <p className="text-xs text-gray-500 line-clamp-2 mb-2">{task.description}</p>
      )}

      {/* Metadata badges */}
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        {/* Type */}
        <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
          <TaskIcon className="h-3 w-3" />
          {getTaskTypeLabel(task.type)}
        </span>

        {/* Due date */}
        {task.due_date && (
          <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${getDueDateColor(task.due_date)}`}>
            <CalendarDaysIcon className="h-3 w-3" />
            {formatDueDate(task.due_date)}
          </span>
        )}

        {/* Recurring badge */}
        {task.is_recurring && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-purple-600">
            <ArrowPathIcon className="h-3 w-3" />
            Terugkerend
          </span>
        )}
      </div>

      {/* Assignment info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-[10px]">
          <UserIcon className="h-3 w-3 text-gray-400" />
          {task.assignment_type === "open_to_all" && !task.claimed_by && (
            <span className="text-green-600 font-medium">Open</span>
          )}
          {task.assignment_type === "open_to_all" && task.claimed_by && (
            <span className="text-blue-600">{task.claimed_by_name || "Opgepakt"}</span>
          )}
          {task.assignment_type === "specific_user" && (
            <span className="text-gray-600">{task.assigned_to_name || "Toegewezen"}</span>
          )}
          {task.assignment_type === "team" && (
            <span className="text-purple-600 font-medium">Team</span>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {task.assignment_type === "open_to_all" &&
            task.status === "pending" &&
            !task.claimed_by &&
            onClaimTask && (
              <button
                onClick={() => onClaimTask(task.id)}
                className="px-2 py-0.5 text-[10px] font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 transition-colors"
              >
                Pak aan
              </button>
            )}
          {task.assignment_type === "open_to_all" &&
            task.claimed_by === currentUserId &&
            onUnclaimTask && (
              <button
                onClick={() => onUnclaimTask(task.id)}
                className="px-2 py-0.5 text-[10px] font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 transition-colors"
              >
                Laat los
              </button>
            )}
        </div>
      </div>

      {/* Completed by */}
      {task.status === "completed" && task.completed_by_name && (
        <p className="mt-1.5 text-[10px] text-gray-400 italic">
          Voltooid door {task.completed_by_name}
        </p>
      )}
    </div>
  );
}

export default function KanbanCard(props: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: props.task.id,
    disabled: !props.canDrag,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{ opacity: isDragging ? 0.3 : 1 }}
    >
      <KanbanCardDisplay {...props} />
    </div>
  );
}
